import type { Sweepstake, SweepstakeDetails } from "@/app/_lib/apis/sweepstake";
import { formatDateOnly } from "@/utils/date";
import { calculateMythicPercentage } from "@/utils/rewards";
import { onDarkBase } from "@biom3/design-tokens";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Link from "next/link";
import { RarityText } from "../RarityText";
import { SweepstakeSelector } from "./SweepstakeSelector";
import { TierSection } from "./TiersSection";
import { imxFormatter } from "./config";
export const OverallPrizeTypes = [
  "IMX",
  "Gems",
  "Free entry to following week's draw",
];

type TiersContentProps = {
  sweepstakes?: Sweepstake[];
  currentSweepstake?: SweepstakeDetails;
};

export function TiersContent({
  sweepstakes = [],
  currentSweepstake,
}: TiersContentProps) {
  const mythicPercentage = calculateMythicPercentage(
    currentSweepstake?.total_imx,
    currentSweepstake?.total_imx_sans_jackpots,
  );

  return (
    <Box
      sx={{
        width: "100%",
        margin: "0 auto",
        padding: "24px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          mb: 2,
          position: "relative",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Link href="/rewards" passHref style={{ textDecoration: "none" }}>
            <Button
              startIcon={<ArrowBackIcon />}
              variant="text"
              sx={{
                color: "text.secondary",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "16px",
                padding: "4px 8px",
                minWidth: "auto",
              }}
            >
              Back to Rewards
            </Button>
          </Link>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="h2">Rewards Tiers</Typography>

          <SweepstakeSelector
            sweepstakes={sweepstakes}
            currentSweepstake={currentSweepstake}
          />
        </Box>
      </Box>
      <Divider
        sx={{
          mb: 5,
        }}
      />
      {/* Overall Prize Pool Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{
            mb: 2,
          }}
        >
          Overall Prize Pool of all draws
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 1,
          }}
        >
          The total prize pool for the weekly cycle from{" "}
          {currentSweepstake?.start_date
            ? formatDateOnly(new Date(currentSweepstake.start_date))
            : ""}{" "}
          to{" "}
          {currentSweepstake?.end_date
            ? formatDateOnly(new Date(currentSweepstake.end_date))
            : ""}{" "}
          is{" "}
          <span style={{ fontWeight: "bold" }}>
            {currentSweepstake?.quest_id === sweepstakes[0].quest_id
              ? `a minimum of ${(40000 / (1.1 * (currentSweepstake?.imx_price || 1))).toLocaleString()} `
              : `${imxFormatter.format(currentSweepstake?.total_imx || 0)} `}
            IMX
          </span>
          . This prize pool includes:
        </Typography>
        <List
          sx={{
            padding: 0,
            listStyleType: "disc",
            paddingLeft: 1.5,
            "& .MuiListItem-root": {
              display: "list-item",
              py: 0,
              paddingLeft: 0.5,
            },
          }}
        >
          {OverallPrizeTypes.map((prizeType) => (
            <ListItem key={prizeType}>
              <ListItemText
                primary={prizeType}
                sx={{
                  margin: 0,
                  "& .MuiListItemText-primary": {
                    "@media (max-width: 385px)": {
                      fontSize: "0.875rem",
                    },
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Tiers Section */}
      <TierSection sweepstake={currentSweepstake} rarity="Common" />
      <TierSection sweepstake={currentSweepstake} rarity="Uncommon" />
      <TierSection sweepstake={currentSweepstake} rarity="Rare" />
      <TierSection sweepstake={currentSweepstake} rarity="Epic" />
      <TierSection sweepstake={currentSweepstake} rarity="Legendary" />

      {/* Jackpot Section */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          bgcolor: onDarkBase.color.neutral[600],
          borderRadius: 6,
        }}
      >
        <Typography variant="h3" fontSize={28} fontWeight="bold" sx={{ mb: 2 }}>
          Prizes available for entrants in all Tiers in Mythic Reward draw
        </Typography>
        <Typography variant="body1">
          {currentSweepstake?.total_imx ? (
            <>
              The total prize pool for the Mythic Rewards is{" "}
              {imxFormatter.format(
                currentSweepstake?.total_imx -
                  (currentSweepstake?.total_imx_sans_jackpots || 0),
              )}{" "}
              IMX.
            </>
          ) : (
            "This Tier contains IMX only."
          )}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Winners are selected based on the Key Tier used to enter. Higher tier
          Keys unlock access to more valuable rewards:
        </Typography>
        <Table sx={{ my: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Winner Count</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Reward Per Winner
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Min Key Tier</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentSweepstake?.jackpots
              ?.sort((a, b) => Number(b.imx_reward) - Number(a.imx_reward))
              .map((jackpot, index) => (
                <TableRow key={`jackpot-tiers-${jackpot.imx_reward}`}>
                  <TableCell>{jackpot.winner_count}</TableCell>
                  <TableCell>
                    {imxFormatter.format(Number(jackpot.imx_reward))} IMX
                  </TableCell>
                  <TableCell>
                    <RarityText
                      rarity={
                        jackpot.min_tier
                          ? jackpot.min_tier
                          : index === 0 || index === 1
                            ? "Legendary"
                            : index === 2 || index === 3
                              ? "Epic"
                              : index === 4
                                ? "Rare"
                                : index === 5
                                  ? "Uncommon"
                                  : "Common"
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
